import { PensionPlanTransfer } from '../api/pension-plan-transfers/interfaces/PensionPlanTransfer';

/** `origin` con el que nacen las peticiones pedidas desde dentro de la plataforma. */
const PLATFORM_ORIGIN = 'PLATFORM';

const LIVE_STATUSES = ['requested', 'draft'] as const;

/**
 * La petición que representa al usuario en el dashboard: la viva si la hay, y
 * si no, la más relevante de las que queden.
 */
export function resolvePrimaryTransfer(
	transfers: PensionPlanTransfer[]
): PensionPlanTransfer | undefined {
	return (
		transfers.find((t) => LIVE_STATUSES.includes(t.status as never)) ??
		transfers.find((t) => t.status === 'in_progress') ??
		transfers.find((t) => t.status === 'completed') ??
		transfers[0]
	);
}

/**
 * ¿Este usuario está a medias de su onboarding y hay que llevarle a elegir plan?
 *
 * 🔴 Un traspaso pedido DESDE la plataforma **no** cuenta, aunque se le parezca:
 * nace `requested` y sin plan bloqueado, igual que un onboarding sin terminar.
 * Al no distinguirlos, el dashboard entero se sustituía por la vista de
 * exploración y a un cliente que ya tiene plan se le pedía «elegir plan e
 * iniciar verificación» — con un botón que lo mandaba a un KYC ya terminado.
 * Quedaba encerrado ahí, sin poder usar su plataforma, justo después de pedirnos
 * traer más dinero (visto en DEV el 11/09/26).
 *
 * Los separa `origin`, y es fiable por construcción: el funnel exige `partner`
 * (@IsNotEmpty en create-b2c-transfer.dto.ts:82) y, cuando no hay, manda el
 * centinela «Directo (sin partner)». Como nunca llega vacío, el default
 * `PLATFORM` de `create()` (pension-plan-transfers.service.ts:175) solo lo toca
 * quien pide desde dentro de la plataforma. Medido en PROD el 11/09/26: de las
 * 156 peticiones vivas sin plan bloqueado, las 155 del funnel llevan partner o
 * centinela y solo la de plataforma lleva `PLATFORM`.
 *
 * ⚠️ Ni `selectedArcaPlanId` ni `kind` sirven aquí, por mucho que lo parezcan, y
 * los dos se probaron:
 *  - `selectedArcaPlanId` está **al revés** de lo que se supone: lo rellena el
 *    funnel con el plan que el lead eligió allí (b2c-funnel.service.ts:177). Lo
 *    traen 141 peticiones vivas del funnel y ninguna de plataforma, así que
 *    usarlo dejaba a esos 141 sin poder elegir plan ni arrancar su KYC.
 *  - `kind` tampoco: el funnel declara `EXTERNAL_TRANSFER` en su traspaso
 *    (b2c-funnel.service.ts:311), el mismo valor que el modal.
 *
 * El precio de usar `origin` es un caso al revés —un onboarding cuya petición
 * naciera con `PLATFORM` se quedaría sin selector—, y está medido: en PROD es
 * UNA, un borrador del 23/01/26 de alguien que nunca llegó a tener plan.
 */
/**
 * ¿Esta petición se pidió desde dentro de la plataforma?
 *
 * Misma señal que usa `isOnboardingExploration`, y por las mismas razones: ver
 * el comentario de abajo antes de cambiarla por `selectedArcaPlanId` o `kind`.
 */
export function isPlatformTransfer(transfer?: PensionPlanTransfer): boolean {
	return transfer?.origin === PLATFORM_ORIGIN;
}

export function isOnboardingExploration(transfer?: PensionPlanTransfer): boolean {
	if (!transfer) return false;
	if (transfer.origin === PLATFORM_ORIGIN) return false;

	return LIVE_STATUSES.includes(transfer.status as never) && !transfer.planLockedAt;
}
